// TUNE Live's units, with its exact factors (crates/tune-values/src/units.rs), so a number the web shows and sends
// back reads the same on the MFD. Values are kept metric and converted only for display. A vehicle's settings name
// each unit as TUNE Live's menu shows it (settings.schema.json).

/** How a value converts for display. */
export type UnitClass =
  | 'none'
  | 'temperature' // kept in °C
  | 'speed' // km/h
  | 'pressure' // bar
  | 'torque' // N·m
  | 'distance' // km
  | 'fuelConsumption' // L/100 km
  | 'volume'; // litres

export const SPEED_UNITS = ['km/h', 'mph', 'm/s', 'knots'] as const;
export const TEMPERATURE_UNITS = ['°C', '°F'] as const;
export const PRESSURE_UNITS = ['bar', 'psi', 'kPa'] as const;
export const DISTANCE_UNITS = ['km', 'miles'] as const;
export const CONSUMPTION_UNITS = ['L/100 km', 'mpg (US)', 'mpg (UK)', 'km/L'] as const;

export type SpeedUnit = (typeof SPEED_UNITS)[number];
export type TemperatureUnit = (typeof TEMPERATURE_UNITS)[number];
export type PressureUnit = (typeof PRESSURE_UNITS)[number];
export type DistanceUnit = (typeof DISTANCE_UNITS)[number];
export type ConsumptionUnit = (typeof CONSUMPTION_UNITS)[number];
/** Fuel volume follows the consumption unit: litres for L/100 km and km/L, US or UK gallons for mpg. */
export type VolumeUnit = 'litres' | 'gallonsUs' | 'gallonsUk';

/** The units a vehicle's settings choose. */
export interface Units {
  speed: SpeedUnit;
  temperature: TemperatureUnit;
  pressure: PressureUnit;
  distance: DistanceUnit;
  consumption: ConsumptionUnit;
  volume: VolumeUnit;
}

/** TUNE Live's factors, the ones legacy theme files were designed around. */
export const FACTORS = {
  kmhToMph: 0.621371,
  kmhToMs: 0.277778,
  kmhToKnots: 0.539957,
  barToPsi: 14.5038,
  barToKpa: 100.0,
  nmToLbft: 0.7375621,
  kmToMi: 0.621371,
  l100MpgUs: 235.214583,
  l100MpgUk: 282.480936,
  litresToGallonsUs: 0.264172,
  litresToGallonsUk: 0.219969,
} as const;

/** km/h, °C, bar, km, L/100 km, litres. */
export const METRIC: Units = {
  speed: 'km/h',
  temperature: '°C',
  pressure: 'bar',
  distance: 'km',
  consumption: 'L/100 km',
  volume: 'litres',
};

/** mph, °F, psi, miles, US mpg, US gallons. */
export const US: Units = {
  speed: 'mph',
  temperature: '°F',
  pressure: 'psi',
  distance: 'miles',
  consumption: 'mpg (US)',
  volume: 'gallonsUs',
};

function pick<T extends string>(value: unknown, options: readonly T[]): T {
  // As TUNE Live does: anything it doesn't know is the first, metric choice.
  return options.find((o) => o === value) ?? (options[0] as T);
}

/** The units a vehicle's settings values choose. */
export function unitsOf(values: Record<string, unknown>): Units {
  const consumption = pick(values.consumptionUnit, CONSUMPTION_UNITS);
  const volume: VolumeUnit =
    consumption === 'mpg (US)' ? 'gallonsUs' : consumption === 'mpg (UK)' ? 'gallonsUk' : 'litres';
  return {
    speed: pick(values.speedUnit, SPEED_UNITS),
    temperature: pick(values.temperatureUnit, TEMPERATURE_UNITS),
    pressure: pick(values.pressureUnit, PRESSURE_UNITS),
    distance: pick(values.distanceUnit, DISTANCE_UNITS),
    consumption,
    volume,
  };
}

/** Torque follows pressure, as legacy theme files expect: lb-ft when pressure is in psi. */
export function torqueUnit(units: Units): 'Nm' | 'lb-ft' {
  return units.pressure === 'psi' ? 'lb-ft' : 'Nm';
}

/** `k / v` for reciprocal units (L/100 km and mpg), with 0 for 0 rather than infinity. */
function reciprocal(k: number, v: number): number {
  return v === 0 ? 0 : k / v;
}

/** A kept (metric) value, as it's shown. */
export function toDisplay(units: Units, unitClass: UnitClass, metric: number): number {
  const f = FACTORS;
  switch (unitClass) {
    case 'none':
      return metric;
    case 'temperature':
      return units.temperature === '°F' ? metric * 1.8 + 32.0 : metric;
    case 'speed':
      switch (units.speed) {
        case 'km/h':
          return metric;
        case 'mph':
          return metric * f.kmhToMph;
        case 'm/s':
          return metric * f.kmhToMs;
        case 'knots':
          return metric * f.kmhToKnots;
      }
      break;
    case 'pressure':
      switch (units.pressure) {
        case 'bar':
          return metric;
        case 'psi':
          return metric * f.barToPsi;
        case 'kPa':
          return metric * f.barToKpa;
      }
      break;
    case 'torque':
      return torqueUnit(units) === 'lb-ft' ? metric * f.nmToLbft : metric;
    case 'distance':
      return units.distance === 'miles' ? metric * f.kmToMi : metric;
    case 'fuelConsumption':
      switch (units.consumption) {
        case 'L/100 km':
          return metric;
        case 'mpg (US)':
          return reciprocal(f.l100MpgUs, metric);
        case 'mpg (UK)':
          return reciprocal(f.l100MpgUk, metric);
        case 'km/L':
          return reciprocal(100.0, metric);
      }
      break;
    case 'volume':
      switch (units.volume) {
        case 'litres':
          return metric;
        case 'gallonsUs':
          return metric * f.litresToGallonsUs;
        case 'gallonsUk':
          return metric * f.litresToGallonsUk;
      }
  }
  return metric;
}

/** A shown value, as it's kept (metric): the exact inverse of `toDisplay`. */
export function fromDisplay(units: Units, unitClass: UnitClass, shown: number): number {
  const f = FACTORS;
  switch (unitClass) {
    case 'none':
      return shown;
    case 'temperature':
      return units.temperature === '°F' ? (shown - 32.0) / 1.8 : shown;
    case 'speed':
      switch (units.speed) {
        case 'km/h':
          return shown;
        case 'mph':
          return shown / f.kmhToMph;
        case 'm/s':
          return shown / f.kmhToMs;
        case 'knots':
          return shown / f.kmhToKnots;
      }
      break;
    case 'pressure':
      switch (units.pressure) {
        case 'bar':
          return shown;
        case 'psi':
          return shown / f.barToPsi;
        case 'kPa':
          return shown / f.barToKpa;
      }
      break;
    case 'torque':
      return torqueUnit(units) === 'lb-ft' ? shown / f.nmToLbft : shown;
    case 'distance':
      return units.distance === 'miles' ? shown / f.kmToMi : shown;
    case 'fuelConsumption':
      switch (units.consumption) {
        case 'L/100 km':
          return shown;
        case 'mpg (US)':
          return reciprocal(f.l100MpgUs, shown);
        case 'mpg (UK)':
          return reciprocal(f.l100MpgUk, shown);
        case 'km/L':
          return reciprocal(100.0, shown);
      }
      break;
    case 'volume':
      switch (units.volume) {
        case 'litres':
          return shown;
        case 'gallonsUs':
          return shown / f.litresToGallonsUs;
        case 'gallonsUk':
          return shown / f.litresToGallonsUk;
      }
  }
  return shown;
}

/** The unit's symbol, as TUNE Live labels values. */
export function symbol(units: Units, unitClass: UnitClass): string {
  switch (unitClass) {
    case 'none':
      return '';
    case 'temperature':
      return units.temperature;
    case 'speed':
      return { 'km/h': 'km/h', mph: 'mph', 'm/s': 'm/s', knots: 'kn' }[units.speed];
    case 'pressure':
      return units.pressure;
    case 'torque':
      return torqueUnit(units);
    case 'distance':
      return units.distance === 'miles' ? 'mi' : 'km';
    case 'fuelConsumption':
      return { 'L/100 km': 'L/100km', 'mpg (US)': 'mpg', 'mpg (UK)': 'mpg (UK)', 'km/L': 'km/L' }[units.consumption];
    case 'volume':
      return { litres: 'L', gallonsUs: 'gal', gallonsUk: 'gal (UK)' }[units.volume];
  }
}

/**
 * A unit written in a config's `units` (crates/tune-values/src/units.rs `UnitHint`): what a value is in once its
 * conversion has run, which TUNE Live brings to its kept, metric form.
 */
export type UnitHint =
  | 'metric'
  | 'kelvin'
  | 'fahrenheit'
  | 'mph'
  | 'metresPerSecond'
  | 'knots'
  | 'psi'
  | 'kpa'
  | 'miles'
  | 'poundFeet'
  | 'mpgUs'
  | 'mpgUk'
  | 'kmPerLitre'
  | 'gallonsUs'
  | 'gallonsUk'
  | 'bit';

/** Each symbol TUNE Live takes, in lower case, and its hint. */
const HINTS: Record<string, UnitHint> = {};
const add = (hint: UnitHint, symbols: string[]) => {
  for (const s of symbols) {
    HINTS[s] = hint;
  }
};
add('metric', [
  '',
  'c',
  '°c',
  'degc',
  'km/h',
  'kmh',
  'kph',
  'bar',
  'km',
  'nm',
  'l/100km',
  'l',
  'litre',
  'liter',
  'litres',
  'liters',
]);
add('kelvin', ['k', 'kelvin']);
add('fahrenheit', ['f', '°f', 'degf']);
add('mph', ['mph', 'mi/h']);
add('metresPerSecond', ['m/s']);
add('knots', ['kn', 'knots', 'kt']);
add('psi', ['psi']);
add('kpa', ['kpa']);
add('miles', ['mi', 'mile', 'miles']);
add('poundFeet', ['lb-ft', 'lbft', 'ft-lb', 'ft-lbs']);
add('mpgUs', ['mpg', 'mpg (us)']);
add('mpgUk', ['mpg (uk)']);
add('kmPerLitre', ['km/l']);
add('gallonsUs', ['gal', 'gal (us)']);
add('gallonsUk', ['gal (uk)']);
add('bit', ['bit', 'bool']);

/** As TUNE Live trims and lower-cases a symbol: Unicode space at the ends, A to Z only. */
function symbolKey(symbol: string): string {
  return symbol.replace(/^\p{White_Space}+|\p{White_Space}+$/gu, '').replace(/[A-Z]/g, (c) => c.toLowerCase());
}

/** A config's `units`, as TUNE Live reads it; null for a symbol it doesn't know (it then uses the value unconverted). */
export function parseUnitHint(symbol: string): UnitHint | null {
  return HINTS[symbolKey(symbol)] ?? null;
}

/** A value in a hint's unit, brought to its kept, metric form. */
export function hintToMetric(hint: UnitHint, v: number): number {
  const f = FACTORS;
  switch (hint) {
    case 'metric':
    case 'bit':
      return v;
    case 'kelvin':
      return v - 273.15;
    case 'fahrenheit':
      return (v - 32.0) / 1.8;
    case 'mph':
      return v / f.kmhToMph;
    case 'metresPerSecond':
      return v / f.kmhToMs;
    case 'knots':
      return v / f.kmhToKnots;
    case 'psi':
      return v / f.barToPsi;
    case 'kpa':
      return v / f.barToKpa;
    case 'miles':
      return v / f.kmToMi;
    case 'poundFeet':
      return v / f.nmToLbft;
    case 'mpgUs':
      return reciprocal(f.l100MpgUs, v);
    case 'mpgUk':
      return reciprocal(f.l100MpgUk, v);
    case 'kmPerLitre':
      return reciprocal(100.0, v);
    case 'gallonsUs':
      return v / f.litresToGallonsUs;
    case 'gallonsUk':
      return v / f.litresToGallonsUk;
  }
}

/** The opposite: a kept value in a hint's unit, as a sent frame carries it. */
export function hintFromMetric(hint: UnitHint, v: number): number {
  const f = FACTORS;
  switch (hint) {
    case 'metric':
    case 'bit':
      return v;
    case 'kelvin':
      return v + 273.15;
    case 'fahrenheit':
      return v * 1.8 + 32.0;
    case 'mph':
      return v * f.kmhToMph;
    case 'metresPerSecond':
      return v * f.kmhToMs;
    case 'knots':
      return v * f.kmhToKnots;
    case 'psi':
      return v * f.barToPsi;
    case 'kpa':
      return v * f.barToKpa;
    case 'miles':
      return v * f.kmToMi;
    case 'poundFeet':
      return v * f.nmToLbft;
    case 'mpgUs':
      return reciprocal(f.l100MpgUs, v);
    case 'mpgUk':
      return reciprocal(f.l100MpgUk, v);
    case 'kmPerLitre':
      return reciprocal(100.0, v);
    case 'gallonsUs':
      return v * f.litresToGallonsUs;
    case 'gallonsUk':
      return v * f.litresToGallonsUk;
  }
}

/** The class a symbol's unit is in, which a custom value with that unit takes (TUNE Live's `class_of_symbol`). */
export function unitClassOfSymbol(symbol: string): UnitClass {
  const s = symbolKey(symbol);
  if (['c', '°c', 'degc', 'k', 'kelvin', 'f', '°f', 'degf'].includes(s)) {
    return 'temperature';
  }
  if (['km/h', 'kmh', 'kph', 'mph', 'mi/h', 'm/s', 'kn', 'knots', 'kt'].includes(s)) {
    return 'speed';
  }
  if (['bar', 'psi', 'kpa'].includes(s)) {
    return 'pressure';
  }
  if (['nm', 'lb-ft', 'lbft', 'ft-lb', 'ft-lbs'].includes(s)) {
    return 'torque';
  }
  if (['km', 'mi', 'mile', 'miles'].includes(s)) {
    return 'distance';
  }
  if (['l/100km', 'mpg', 'mpg (us)', 'mpg (uk)', 'km/l'].includes(s)) {
    return 'fuelConsumption';
  }
  if (['l', 'litre', 'liter', 'litres', 'liters', 'gal', 'gal (us)', 'gal (uk)'].includes(s)) {
    return 'volume';
  }
  return 'none';
}

/** Where TUNE Live keeps each class: what a built-in value's formula must give. */
export const KEPT_IN: Record<UnitClass, string> = {
  none: '',
  temperature: '°C',
  speed: 'km/h',
  pressure: 'bar',
  torque: 'N·m',
  distance: 'km',
  fuelConsumption: 'L/100 km',
  volume: 'litres',
};

/** The units the config creator offers, by class, each as a config writes it. */
export const UNIT_CHOICES: ReadonlyArray<{ group: UnitClass | 'switch'; symbol: string; label: string }> = [
  { group: 'temperature', symbol: 'C', label: '°C' },
  { group: 'temperature', symbol: 'K', label: 'K (kelvin)' },
  { group: 'temperature', symbol: 'F', label: '°F' },
  { group: 'speed', symbol: 'km/h', label: 'km/h' },
  { group: 'speed', symbol: 'mph', label: 'mph' },
  { group: 'speed', symbol: 'm/s', label: 'm/s' },
  { group: 'speed', symbol: 'kn', label: 'knots' },
  { group: 'pressure', symbol: 'bar', label: 'bar' },
  { group: 'pressure', symbol: 'kPa', label: 'kPa' },
  { group: 'pressure', symbol: 'psi', label: 'psi' },
  { group: 'torque', symbol: 'Nm', label: 'N·m' },
  { group: 'torque', symbol: 'lb-ft', label: 'lb-ft' },
  { group: 'distance', symbol: 'km', label: 'km' },
  { group: 'distance', symbol: 'mi', label: 'miles' },
  { group: 'fuelConsumption', symbol: 'L/100km', label: 'L/100 km' },
  { group: 'fuelConsumption', symbol: 'mpg', label: 'mpg (US)' },
  { group: 'fuelConsumption', symbol: 'mpg (UK)', label: 'mpg (UK)' },
  { group: 'fuelConsumption', symbol: 'km/L', label: 'km/L' },
  { group: 'volume', symbol: 'L', label: 'litres' },
  { group: 'volume', symbol: 'gal', label: 'gallons (US)' },
  { group: 'volume', symbol: 'gal (UK)', label: 'gallons (UK)' },
  { group: 'switch', symbol: 'bit', label: 'On or off (a switch)' },
];
